import { Injectable, effect, signal } from '@angular/core';

/** The two themes. Anything else in the cookie is treated as absent. */
export type Theme = 'light' | 'dark';

export const THEME_COOKIE_NAME = 'hubCentral.theme';

/** A year. Long enough that the choice reads as permanent; a session cookie would forget it on every close. */
const THEME_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export const DEFAULT_THEME: Theme = 'light';

export function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark';
}

/**
 * Finds the theme in a `document.cookie` string.
 *
 * Parsed rather than matched with a single regular expression because `hubCentral.theme` is a prefix of
 * nothing today but `hubCentral.themePreview` would silently match one, and because a cookie value can
 * legitimately contain `=`. Splitting on the first `=` only is what makes both safe.
 *
 * Returns null for a missing or unrecognised value, so the caller decides the default rather than having
 * 'light' asserted from a corrupt cookie.
 */
export function readThemeCookie(cookieString: string | null | undefined): Theme | null {
  if (!cookieString) return null;

  for (const part of cookieString.split(';')) {
    const separator = part.indexOf('=');
    if (separator < 0) continue;

    if (part.slice(0, separator).trim() !== THEME_COOKIE_NAME) continue;

    const value = decodeURIComponent(part.slice(separator + 1).trim());
    return isTheme(value) ? value : null;
  }

  return null;
}

/**
 * Builds the cookie assignment string.
 *
 * `path=/` so the preference survives navigating between /user, /approver and /admin - scoped to the current
 * path it would appear to reset itself on every role change.
 *
 * `SameSite=Lax` because nothing needs to read it cross-site, and `Secure` only over HTTPS: setting Secure on
 * `http://localhost` makes the browser discard the cookie outright, so the toggle would not persist in local
 * development.
 */
export function themeCookie(theme: Theme, secure: boolean): string {
  const attributes = [
    `${THEME_COOKIE_NAME}=${theme}`,
    'path=/',
    `max-age=${THEME_COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];

  if (secure) attributes.push('Secure');

  return attributes.join('; ');
}

/**
 * The theme to start in: the cookie if one has been written, otherwise light.
 *
 * The operating system's `prefers-color-scheme` is deliberately NOT consulted. It used to be, so a user whose
 * machine is dark got a dark portal on their first visit - which made the product's default appearance a
 * property of the visitor's laptop rather than a decision, and meant two people opening the same screen to
 * compare it saw different colours. Light is the designed default; dark remains one toggle away and, once
 * chosen, is remembered by the cookie.
 *
 * Pure, so both callers - the service and the pre-bootstrap hook - resolve it identically, and so the
 * precedence is testable without a browser.
 */
export function resolveInitialTheme(cookieString: string | null | undefined): Theme {
  return readThemeCookie(cookieString) ?? DEFAULT_THEME;
}

/**
 * Puts the stored theme on <html> without waiting for Angular.
 *
 * Called from main.ts before bootstrap so the page is painted in the right colours from the start. It only
 * reads; the cookie is written when the user actually chooses, which is the service's job.
 */
export function applyStoredTheme(): void {
  if (typeof document === 'undefined') return;

  document.documentElement.dataset['theme'] = resolveInitialTheme(document.cookie);
}

/**
 * The chosen colour theme, persisted in a cookie and applied as `data-theme` on <html>.
 *
 * The attribute is the only thing the stylesheet reads - see styles/base/_theme.scss, where
 * `[data-theme='dark']` redefines the custom properties that every colour token resolves to.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly current = signal<Theme>(this.restore());

  /** Read-only view for templates. */
  readonly theme = this.current.asReadonly();

  constructor() {
    effect(() => this.apply(this.current()));
  }

  isDark(): boolean {
    return this.current() === 'dark';
  }

  set(theme: Theme): void {
    this.current.set(theme);
  }

  toggle(): void {
    this.current.update((theme) => (theme === 'dark' ? 'light' : 'dark'));
  }

  private restore(): Theme {
    return resolveInitialTheme(typeof document === 'undefined' ? null : document.cookie);
  }

  private apply(theme: Theme): void {
    if (typeof document === 'undefined') return;

    document.documentElement.dataset['theme'] = theme;

    try {
      document.cookie = themeCookie(theme, location.protocol === 'https:');
    } catch {
      // Cookies can be blocked outright. The theme still applies for this visit; it just will not be
      // remembered, which is not worth interrupting the user over.
    }
  }
}
